<template>
  <div v-if="visible">
    <AppHeader>
      <span class="mobile-smaller-heading">
        {{
          $t(
            "events:common.registrationRulesAndLimits.section.heading",
            "Additional registration rules and limits"
          )
        }}</span
      >
    </AppHeader>
    <div class="bg-remove-container-padding">
      <div>
        <AppTableHeader single-col class="d-print-none">
          <template #left>
            <span class="mobile-smaller-font">
              {{
                $t(
                  "events:common.registrationRulesAndLimits.limits.heading",
                  "Registration limits"
                )
              }}
            </span>
          </template>
        </AppTableHeader>
        <table class="entsys--table event-limits-mobile-table">
          <colgroup v-if="desktopView">
            <col style="width: 20em" />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr v-if="desktopView">
              <th class="text-start" style="font-size: 12px !important">
                {{
                  $t(
                    "events:common.registrationRulesAndLimits.rules.label",
                    "Rules"
                  )
                }}
              </th>
              <th class="text-start" style="font-size: 12px !important">
                {{
                  $t(
                    "events:common.registrationRulesAndLimits.details.label",
                    "Details"
                  )
                }}
              </th>
              <th class="text-center" style="font-size: 12px !important">
                {{
                  $t(
                    "events:common.registrationRulesAndLimits.maxEntries.label",
                    "Maximum number of entries"
                  )
                }}
              </th>
              <th class="text-center" style="font-size: 12px !important">
                {{
                  $t(
                    "events:common.registrationRulesAndLimits.maxAthletes.label",
                    "Maximum number of athletes"
                  )
                }}
              </th>
              <th class="text-center" style="font-size: 12px !important">
                {{
                  $t(
                    "events:common.registrationRulesAndLimits.maxHorsesPerAthlete.label",
                    "Max horses per athlete"
                  )
                }}
              </th>
              <th class="text-center" style="font-size: 12px !important">
                {{
                  $t(
                    "events:common.registrationRulesAndLimits.maxEntriesPerHorse.label",
                    "Max entries per horse"
                  )
                }}
              </th>
              <th class="d-print-none" />
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(eventLimit, index) in eventLimits"
              :key="eventLimit.id"
            >
              <tr v-if="desktopView" data-test="event-limit-row">
                <td>
                  <template v-if="eventLimit.limitScope == 'WHOLE_EVENT'">
                    {{
                      $t(
                        "events:common.registrationRulesAndLimits.limitForWholeEvent.text",
                        "Registration limit for the whole event"
                      )
                    }}
                  </template>
                  <template v-else-if="eventLimit.limitScope == 'COMPETITIONS'">
                    {{
                      $t(
                        "events:common.registrationRulesAndLimits.limitForGroupOfCompetitions.text",
                        "Registration limit for group of competitions (sum of entries for selected competitions)"
                      )
                    }}
                  </template>
                </td>
                <td>
                  <template v-if="eventLimit.limitScope == 'COMPETITIONS'">
                    {{
                      eventLimitCompetitionsSorted(eventLimit.competitionIds)
                    }}
                  </template>
                </td>
                <td class="text-center">
                  <template v-if="eventLimit.maxNumberOfEntries">
                    <AppShowChipBoxes
                      :items="[
                        {
                          name: 'confirmed',
                          value: eventLimit.entriesRegistrationConfirmedCount
                        },
                        {
                          name: 'max',
                          value: eventLimit.maxNumberOfEntries
                        }
                      ]"
                    />
                  </template>
                  <template v-else>
                    {{
                      $t(
                        "events:common.registrationRulesAndLimits.notSet.text",
                        "not set"
                      )
                    }}
                  </template>
                </td>
                <td class="text-center">
                  <template v-if="eventLimit.maxNumberOfAthletes">
                    <AppShowChipBoxes
                      :items="[
                        {
                          name: 'confirmed',
                          value: eventLimit.athletesRegistrationConfirmedCount
                        },
                        {
                          name: 'max',
                          value: eventLimit.maxNumberOfAthletes
                        }
                      ]"
                    />
                  </template>
                  <template v-else>
                    {{
                      $t(
                        "events:common.registrationRulesAndLimits.notSet.text",
                        "not set"
                      )
                    }}
                  </template>
                </td>
                <td class="text-center">
                  <template v-if="eventLimit.maxNumberOfHorsesPerAthlete">
                    <AppShowChipBoxes
                      :items="[
                        {
                          name: 'max',
                          value: eventLimit.maxNumberOfHorsesPerAthlete
                        }
                      ]"
                    />
                  </template>
                  <template v-else>
                    {{
                      $t(
                        "events:common.registrationRulesAndLimits.notSet.text",
                        "not set"
                      )
                    }}
                  </template>
                </td>
                <td class="text-center">
                  <template v-if="eventLimit.maxNumberOfEntriesPerHorse">
                    <AppShowChipBoxes
                      :items="[
                        {
                          name: 'max',
                          value: eventLimit.maxNumberOfEntriesPerHorse
                        }
                      ]"
                    />
                  </template>
                  <template v-else>
                    {{
                      $t(
                        "events:common.registrationRulesAndLimits.notSet.text",
                        "not set"
                      )
                    }}
                  </template>
                </td>
                <td class="text-right d-print-none">
                  <v-btn-icon
                    v-if="canUpdate(eventLimit)"
                    class="bg-edit-green ma-2"
                    :aria-label="$t('common:editButton.alt')"
                    data-test="openEventLimitForm"
                    size="small"
                    icon
                    @click="openEventLimitForm(eventLimit)"
                  >
                    <v-icon color="white" size="small"> $edit </v-icon>
                  </v-btn-icon>
                  <v-btn-icon
                    v-if="
                      $allowedMutation(
                        eventLimit.allowedMutations,
                        'eventLimitDelete'
                      )
                    "
                    class="bg-remove ma-2"
                    :aria-label="$t('common:deleteButton.alt')"
                    data-test="removeEventLimit"
                    :loading="deleteLoading == eventLimit.id"
                    :disabled="!!deleteLoading"
                    size="small"
                    icon
                    @click="deleteEventLimit(eventLimit)"
                  >
                    <v-icon color="white" size="small"> $iconTrash </v-icon>
                  </v-btn-icon>
                  <v-btn-icon
                    v-if="eventLimits.length - 1 == index && canAdd(eventLimit)"
                    class="bg-edit ma-2"
                    :aria-label="$t('common:createButton.alt')"
                    data-test="newEventCompetitionLimitForm"
                    size="small"
                    icon
                    @click="newEventCompetitionLimitForm(eventLimit)"
                  >
                    <v-icon color="white" size="small"> $iconPlus </v-icon>
                  </v-btn-icon>
                </td>
              </tr>
              <tr v-else>
                <td>
                  <div class="my-3">
                    <b>
                      <template v-if="eventLimit.limitScope == 'WHOLE_EVENT'">
                        {{
                          $t(
                            "events:common.registrationRulesAndLimits.limitForWholeEvent.text",
                            "Registration limit for the whole event"
                          )
                        }}
                      </template>
                      <template
                        v-else-if="eventLimit.limitScope == 'COMPETITIONS'"
                      >
                        {{
                          $t(
                            "events:common.registrationRulesAndLimits.limitForGroupOfCompetitions.text",
                            "Registration limit for group of competitions (sum of entries for selected competitions)"
                          )
                        }}
                      </template>
                    </b>
                  </div>

                  <div
                    v-if="eventLimit.limitScope == 'COMPETITIONS'"
                    class="my-3"
                  >
                    <v-row no-gutters>
                      <v-col cols="8" align-self="center">
                        {{
                          $t(
                            "events:common.registrationRulesAndLimits.details.label",
                            "Details"
                          )
                        }}
                      </v-col>
                      <v-col cols="4" class="text-center">
                        {{
                          eventLimitCompetitionsSorted(
                            eventLimit.competitionIds
                          )
                        }}
                      </v-col>
                    </v-row>
                  </div>

                  <div class="my-3">
                    <v-row no-gutters>
                      <v-col cols="8" align-self="center">
                        {{
                          $t(
                            "events:common.registrationRulesAndLimits.maxEntries.label",
                            "Maximum number of entries"
                          )
                        }}
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <template v-if="eventLimit.maxNumberOfEntries">
                          <AppShowChipBoxes
                            :items="[
                              {
                                name: 'confirmed',
                                value:
                                  eventLimit.entriesRegistrationConfirmedCount
                              },
                              {
                                name: 'max',
                                value: eventLimit.maxNumberOfEntries
                              }
                            ]"
                          />
                        </template>
                        <template v-else>
                          {{
                            $t(
                              "events:common.registrationRulesAndLimits.notSet.text",
                              "not set"
                            )
                          }}
                        </template>
                      </v-col>
                    </v-row>
                  </div>

                  <div class="my-3">
                    <v-row no-gutters>
                      <v-col cols="8" align-self="center">
                        {{
                          $t(
                            "events:common.registrationRulesAndLimits.maxAthletes.label",
                            "Maximum number of athletes"
                          )
                        }}
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <template v-if="eventLimit.maxNumberOfAthletes">
                          <AppShowChipBoxes
                            :items="[
                              {
                                name: 'confirmed',
                                value:
                                  eventLimit.athletesRegistrationConfirmedCount
                              },
                              {
                                name: 'max',
                                value: eventLimit.maxNumberOfAthletes
                              }
                            ]"
                          />
                        </template>
                        <template v-else>
                          {{
                            $t(
                              "events:common.registrationRulesAndLimits.notSet.text",
                              "not set"
                            )
                          }}
                        </template>
                      </v-col>
                    </v-row>
                  </div>

                  <div class="my-3">
                    <v-row no-gutters>
                      <v-col cols="8" align-self="center">
                        {{
                          $t(
                            "events:common.registrationRulesAndLimits.maxHorsesPerAthlete.label",
                            "Max horses per athlete"
                          )
                        }}
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <template v-if="eventLimit.maxNumberOfHorsesPerAthlete">
                          <AppShowChipBoxes
                            :items="[
                              {
                                name: 'max',
                                value: eventLimit.maxNumberOfHorsesPerAthlete
                              }
                            ]"
                          />
                        </template>
                        <template v-else>
                          {{
                            $t(
                              "events:common.registrationRulesAndLimits.notSet.text",
                              "not set"
                            )
                          }}
                        </template>
                      </v-col>
                    </v-row>
                  </div>

                  <div class="my-3">
                    <v-row no-gutters>
                      <v-col cols="8" align-self="center">
                        {{
                          $t(
                            "events:common.registrationRulesAndLimits.maxEntriesPerHorse.label",
                            "Max entries per horse"
                          )
                        }}
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <template v-if="eventLimit.maxNumberOfEntriesPerHorse">
                          <AppShowChipBoxes
                            :items="[
                              {
                                name: 'max',
                                value: eventLimit.maxNumberOfEntriesPerHorse
                              }
                            ]"
                          />
                        </template>
                        <template v-else>
                          {{
                            $t(
                              "events:common.registrationRulesAndLimits.notSet.text",
                              "not set"
                            )
                          }}
                        </template>
                      </v-col>
                    </v-row>
                  </div>
                </td>
                <td class="text-end d-print-none" style="width: 0">
                  <div>
                    <v-btn-icon
                      v-if="canUpdate(eventLimit)"
                      class="bg-edit-green ma-2"
                      :aria-label="$t('common:editButton.alt')"
                      data-test="openEventLimitForm"
                      size="small"
                      icon
                      @click="openEventLimitForm(eventLimit)"
                    >
                      <v-icon color="white" size="small"> $edit </v-icon>
                    </v-btn-icon>
                    <v-btn-icon
                      v-if="
                        $allowedMutation(
                          eventLimit.allowedMutations,
                          'eventLimitDelete'
                        )
                      "
                      class="bg-remove ma-2"
                      :aria-label="$t('common:deleteButton.alt')"
                      data-test="removeEventLimit"
                      :loading="deleteLoading == eventLimit.id"
                      :disabled="!!deleteLoading"
                      size="small"
                      icon
                      @click="deleteEventLimit(eventLimit)"
                    >
                      <v-icon color="white" size="small"> $iconTrash </v-icon>
                    </v-btn-icon>
                    <v-btn-icon
                      v-if="canAdd(eventLimit)"
                      class="bg-edit ma-2"
                      :aria-label="$t('common:createButton.alt')"
                      data-test="newEventCompetitionLimitForm"
                      size="small"
                      icon
                      @click="newEventCompetitionLimitForm(eventLimit)"
                    >
                      <v-icon color="white" size="small"> $iconPlus </v-icon>
                    </v-btn-icon>
                  </div>
                </td>
              </tr>

              <tr class="line hidden-md-and-up" />

              <tr
                v-if="eventLimitForm.openForm == eventLimit.id"
                data-test="eventLimitForm"
                class="no-hover event-limit-form"
              >
                <td colspan="99" class="pa-0">
                  <v-sheet color="strong" class="px-6 py-5">
                    <v-form :disabled="loading">
                      <template
                        v-if="eventLimitForm.limitScope == 'COMPETITIONS'"
                      >
                        <h3 class="mb-5">
                          {{
                            $t(
                              "events:common.registrationRulesAndLimits.limitGroupOfCompetitions.subheading",
                              "Select competitions"
                            )
                          }}
                        </h3>

                        <v-row dense>
                          <v-col cols="12" md="3">
                            <v-autocomplete
                              v-model="eventLimitForm.competitionIds"
                              :items="competitionSelections"
                              multiple
                              :label="
                                $t(
                                  'events:common.registrationRulesAndLimits.limitGroupOfCompetitions.label',
                                  'Competitions for group'
                                )
                              "
                              :hint="
                                $t(
                                  'events:common.registrationRulesAndLimits.limitGroupOfCompetitions.hint',
                                  'Limit applies over total number entries and/or unique athletes over the selected competitions.'
                                )
                              "
                              persistent-hint
                              variant="outlined"
                              item-title="code"
                              item-value="id"
                              class="fipa-filter-custom-input fipa-input-white-background"
                            >
                              <template #selection="{ item, index: i }">
                                {{ item.raw.code
                                }}{{
                                  i < eventLimitForm.competitionIds.length - 1
                                    ? ", "
                                    : ""
                                }}
                              </template>
                              <template #item="{ props: attrs, item }">
                                <v-list-item v-bind="attrs">
                                  <template #prepend="{ isActive }">
                                    <v-list-item-action>
                                      <v-checkbox-btn
                                        color="accent"
                                        :model-value="isActive"
                                      />
                                    </v-list-item-action>
                                  </template>
                                  <template #title>
                                    <v-list-item-title>{{
                                      competitionDetails(item.raw)
                                    }}</v-list-item-title>
                                  </template>
                                </v-list-item>
                              </template>
                              >
                            </v-autocomplete>
                          </v-col>
                        </v-row>
                      </template>
                      <h3 class="mb-5">
                        {{ inputFieldTexts.subHeading }}
                      </h3>
                      <v-row dense>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="eventLimitForm.maxNumberOfEntries"
                            :label="inputFieldTexts.entries"
                            :hint="inputFieldTexts.entriesHint"
                            persistent-hint
                            variant="outlined"
                            class="fipa-filter-custom-input fipa-input-white-background"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="eventLimitForm.maxNumberOfAthletes"
                            :label="inputFieldTexts.athletes"
                            :hint="inputFieldTexts.athletesHint"
                            persistent-hint
                            variant="outlined"
                            class="fipa-filter-custom-input fipa-input-white-background"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="
                              eventLimitForm.maxNumberOfHorsesPerAthlete
                            "
                            :label="inputFieldTexts.horsesPerAthlete"
                            :hint="inputFieldTexts.horsesPerAthleteHint"
                            persistent-hint
                            variant="outlined"
                            class="fipa-filter-custom-input fipa-input-white-background"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="
                              eventLimitForm.maxNumberOfEntriesPerHorse
                            "
                            :label="inputFieldTexts.maxNumberOfEntriesPerHorse"
                            :hint="
                              inputFieldTexts.maxNumberOfEntriesPerHorseHint
                            "
                            persistent-hint
                            variant="outlined"
                            class="fipa-filter-custom-input fipa-input-white-background"
                          />
                        </v-col>
                      </v-row>

                      <template v-if="$vuetify.display.lgAndUp">
                        <div class="mt-3 d-flex justify-end">
                          <v-btn
                            size="large"
                            variant="outlined"
                            :disabled="loading"
                            min-width="160px"
                            class="mr-3"
                            @click="closeEventRuleLimitForm()"
                          >
                            {{
                              $t(
                                "common:button.cancel",
                                "Cancel",
                                "Cancel button"
                              )
                            }}
                          </v-btn>
                          <v-btn
                            size="large"
                            min-width="160px"
                            color="accent"
                            :loading="loading"
                            :disabled="
                              loading || requireTwoCompetitionsToProceed
                            "
                            @click="handleEventLimitSubmit()"
                          >
                            {{
                              $t("common:button.save", "Save", "Save button")
                            }}
                          </v-btn>
                        </div>
                      </template>
                      <div
                        v-else
                        class="d-flex flex-column"
                        style="align-items: center"
                      >
                        <v-btn
                          variant="outlined"
                          :disabled="loading"
                          rounded
                          class="mb-5 mt-2"
                          max-width="295px"
                          width="100%"
                          @click="closeEventRuleLimitForm()"
                        >
                          {{
                            $t(
                              "common:button.cancel",
                              "Cancel",
                              "Cancel button"
                            )
                          }}
                        </v-btn>

                        <v-btn
                          max-width="295px"
                          rounded
                          width="100%"
                          color="accent"
                          data-test="submitForm"
                          :loading="loading"
                          :disabled="loading || requireTwoCompetitionsToProceed"
                          @click="handleEventLimitSubmit()"
                        >
                          {{ $t("common:button.save", "Save", "Save button") }}
                        </v-btn>
                      </div>
                    </v-form>
                  </v-sheet>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <br />
    <p class="text-muted">
      {{
        $t(
          "events:common.registrationRulesAndLimits.section.hint",
          "Check daily limits and limits set up for individual competitions from the schedule. "
        )
      }}
    </p>
  </div>
</template>
<script>
import gql from "fraql";
import { cloneDeep, clone } from "lodash-es";

import { useCompetitionHelpers } from "@composables/competition_helpers";

import AppHeader from "@elements/AppHeader.vue";
import AppTableHeader from "@/components/elements/table/AppTableHeader.vue";
import AppShowChipBoxes from "@AppShows/AppShowChipBoxes.vue";

export default {
  name: "EventLimits",
  components: {
    AppHeader,
    AppTableHeader,
    AppShowChipBoxes
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    forceDesktop: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const compHelpers = useCompetitionHelpers();
    return { ...compHelpers };
  },
  data() {
    return {
      loading: false,
      deleteLoading: null,
      eventLimitForm: {
        openForm: null,
        id: null,
        limitScope: null,
        maxNumberOfEntries: null,
        maxNumberOfAthletes: null,
        maxNumberOfHorsesPerAthlete: null,
        maxNumberOfEntriesPerHorse: null,
        competitionIds: []
      }
    };
  },
  computed: {
    desktopView() {
      return this.forceDesktop || this.$vuetify.display.mdAndUp;
    },
    visible() {
      return (
        this.event.eventLimits.length ||
        this.$allowedMutation(this.event.allowedMutations, "eventLimitCreate")
      );
    },
    eventLimits() {
      const [eventLimits, competitionLimits] = cloneDeep(
        this.event.eventLimits
      ).reduce(
        (groups, limit) => {
          switch (limit.limitScope) {
            case "WHOLE_EVENT":
              groups[0].push(limit);
              break;
            case "COMPETITIONS":
              groups[1].push(limit);
              break;
          }

          return groups;
        },
        [[], []]
      );

      if (
        this.$allowedMutation(this.event.allowedMutations, "eventLimitCreate")
      ) {
        if (eventLimits.length == 0) {
          eventLimits.unshift({
            id: "new-event-limit",
            limitScope: "WHOLE_EVENT",
            maxNumberOfEntries: null,
            maxNumberOfAthletes: null,
            maxNumberOfHorsesPerAthlete: null,
            maxNumberOfEntriesPerHorse: null,
            competitionIds: [],
            allowedMutations: []
          });
        }

        if (competitionLimits.length == 0) {
          competitionLimits.unshift({
            id: "new-competition-limit",
            limitScope: "COMPETITIONS",
            maxNumberOfEntries: null,
            maxNumberOfAthletes: null,
            maxNumberOfHorsesPerAthlete: null,
            maxNumberOfEntriesPerHorse: null,
            competitionIds: [],
            createdAt: 0,
            allowedMutations: []
          });
        }
      }

      return [
        ...eventLimits,
        ...competitionLimits.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        )
      ];
    },
    competitionSelections() {
      return clone(this.event.competitions).sort((a, b) =>
        this.competitionOrderFn(a.code, b.code)
      );
    },
    requireTwoCompetitionsToProceed() {
      if (this.eventLimitForm.limitScope == "COMPETITIONS") {
        return this.eventLimitForm.competitionIds.length < 2;
      }
      return false;
    },
    inputFieldTexts() {
      switch (this.eventLimitForm.limitScope) {
        case "COMPETITIONS":
          return {
            subHeading: this.$t(
              "events:common.registrationRulesAndLimits.setUpLimits.subheading",
              "Set up limits for registration"
            ),
            entries: this.$t(
              "events:common.registrationRulesAndLimits.limitEntriesGroupOfCompetitions.label",
              "Limit number of entries"
            ),
            entriesHint: this.$t(
              "events:common.registrationRulesAndLimits.limitEntriesGroupOfCompetitions.hint",
              "When limit is set, registration to selected competitions will be restricted once total number of paid / confirmed entries reaches the set limit."
            ),
            athletes: this.$t(
              "events:common.registrationRulesAndLimits.limitAthletesGroupOfCompetitions.label",
              "Limit number of unique athletes"
            ),
            athletesHint: this.$t(
              "events:common.registrationRulesAndLimits.limitAthletesGroupOfCompetitions.hint",
              "When limit is set, registration to selected competitions will be restricted once total number of unique athletes in paid / confirmed entries reaches the set limit."
            ),
            horsesPerAthlete: this.$t(
              "events:CompetitionForm.input.maxNumberOfHorsesPerAthlete.label",
              "Limit horses per athlete for public registration"
            ),
            horsesPerAthleteHint: this.$t(
              "events:CompetitionForm.input.maxNumberOfHorsesPerAthlete.hint",
              "When limit is set, then entries to the competition will be restricted once number of horses in confirmed entries reaches the set limit."
            ),
            maxNumberOfEntriesPerHorse: this.$t(
              "events:common.registrationRulesAndLimits.limitEntriesPerHorseGroupOfCompetitions.label",
              "Limit number of entries per horse"
            ),
            maxNumberOfEntriesPerHorseHint: this.$t(
              "events:common.registrationRulesAndLimits.limitEntriesPerHorseGroupOfCompetitions.hint",
              "When limit is set, entries to the group of competitions will be restricted once number of entries per horse reaches the limit."
            )
          };
        case "WHOLE_EVENT":
          return {
            subHeading: this.$t(
              "events:common.registrationRulesAndLimits.setUpLimits.subheading",
              "Set up limits for registration"
            ),
            entries: this.$t(
              "events:common.registrationRulesAndLimits.limitEntries.label",
              "Limit number of entries"
            ),
            entriesHint: this.$t(
              "events:common.registrationRulesAndLimits.limitEntries.hint",
              "When limit is set, registration to the event will be restricted once number of paid / confirmed entries reaches the set limit."
            ),
            athletes: this.$t(
              "events:common.registrationRulesAndLimits.limitAthletes.label",
              "Limit number of unique athletes"
            ),
            athletesHint: this.$t(
              "events:common.registrationRulesAndLimits.limitAthletes.hint",
              "When limit is set, registration to the event will be restricted once number of unique athletes in paid / confirmed entries reaches the set limit."
            ),
            horsesPerAthlete: this.$t(
              "events:CompetitionForm.input.maxNumberOfHorsesPerAthlete.label",
              "Limit horses per athlete for public registration"
            ),
            horsesPerAthleteHint: this.$t(
              "events:CompetitionForm.input.maxNumberOfHorsesPerAthlete.hint",
              "When limit is set, then entries to the competition will be restricted once number of horses in confirmed entries reaches the set limit."
            ),
            maxNumberOfEntriesPerHorse: this.$t(
              "events:common.registrationRulesAndLimits.limitEntriesPerHorse.label",
              "Limit number of entries per horse"
            ),
            maxNumberOfEntriesPerHorseHint: this.$t(
              "events:common.registrationRulesAndLimits.limitEntriesPerHorse.hint",
              "When limit is set, entries to the event will be restricted once number of entries per horse reaches the limit."
            )
          };
      }
      return {};
    }
  },
  methods: {
    competitionDetails(c) {
      return [
        c.code,
        c.competitionLevel?.name,
        c.difficultyLevel?.displayName,
        c.competitionRule?.name
      ].join(" | ");
    },
    canAdd(eventLimit) {
      if (eventLimit.limitScope == "COMPETITIONS") {
        return this.$allowedMutation(
          this.event.allowedMutations,
          "eventLimitCreate"
        );
      }
      return false;
    },
    canUpdate(eventLimit) {
      if (eventLimit.limitScope == "COMPETITIONS") {
        return !!(
          eventLimit.id &&
          this.$allowedMutation(eventLimit.allowedMutations, [
            "eventLimitUpdate"
          ])
        );
      }

      return (
        this.$allowedMutation(eventLimit.allowedMutations, [
          "eventLimitUpdate"
        ]) ||
        this.$allowedMutation(this.event.allowedMutations, "eventLimitCreate")
      );
    },

    eventLimitCompetitionsSorted(competitionIds) {
      const competitions = cloneDeep(this.event.competitions).filter(c =>
        competitionIds.includes(c.id)
      );
      return competitions
        .sort((a, b) => this.competitionOrderFn(a.code, b.code))
        .map(c => c.code)
        .join(" + ");
    },
    openEventLimitForm(eventLimit) {
      if (
        this.eventLimitForm.openForm == eventLimit.id &&
        this.eventLimitForm.id // this ensures that user wants to close the edit form
      ) {
        return this.closeEventRuleLimitForm();
      }
      this.eventLimitForm = {
        openForm: eventLimit.id, // Will show form below the row where "Edit" was clicked
        id: eventLimit.id,
        limitScope: eventLimit.limitScope,
        maxNumberOfEntries: eventLimit.maxNumberOfEntries,
        maxNumberOfAthletes: eventLimit.maxNumberOfAthletes,
        maxNumberOfHorsesPerAthlete: eventLimit.maxNumberOfHorsesPerAthlete,
        maxNumberOfEntriesPerHorse: eventLimit.maxNumberOfEntriesPerHorse,
        competitionIds: eventLimit.competitionIds
      };
    },
    newEventCompetitionLimitForm(eventLimit) {
      if (
        this.eventLimitForm.openForm == eventLimit.id &&
        this.eventLimitForm.id == null // this ensures that user wants to close the new form
      ) {
        return this.closeEventRuleLimitForm();
      }
      this.eventLimitForm = {
        openForm: eventLimit.id, // Will show form below the row where "Add" was clicked
        id: null,
        limitScope: "COMPETITIONS",
        maxNumberOfEntries: 0,
        maxNumberOfAthletes: 0,
        maxNumberOfHorsesPerAthlete: 0,
        maxNumberOfEntriesPerHorse: 0,
        competitionIds: []
      };
    },
    closeEventRuleLimitForm() {
      this.eventLimitForm = {
        openForm: false,
        id: null,
        limitScope: null,
        maxNumberOfEntries: null,
        maxNumberOfAthletes: null,
        maxNumberOfHorsesPerAthlete: null,
        maxNumberOfEntriesPerHorse: null,
        competitionIds: []
      };
    },

    handleEventLimitSubmit() {
      this.loading = true;

      this.eventLimitForm.maxNumberOfEntries = Number(
        this.eventLimitForm.maxNumberOfEntries
      );
      this.eventLimitForm.maxNumberOfAthletes = Number(
        this.eventLimitForm.maxNumberOfAthletes
      );
      this.eventLimitForm.maxNumberOfHorsesPerAthlete = Number(
        this.eventLimitForm.maxNumberOfHorsesPerAthlete
      );

      this.eventLimitForm.maxNumberOfEntriesPerHorse = Number(
        this.eventLimitForm.maxNumberOfEntriesPerHorse
      );

      if (
        !this.eventLimitForm.maxNumberOfEntries ||
        Number.isNaN(this.eventLimitForm.maxNumberOfEntries) ||
        this.eventLimitForm.maxNumberOfEntries <= 0
      ) {
        this.eventLimitForm.maxNumberOfEntries = null;
      }
      if (
        !this.eventLimitForm.maxNumberOfEntriesPerHorse ||
        Number.isNaN(this.eventLimitForm.maxNumberOfEntriesPerHorse) ||
        this.eventLimitForm.maxNumberOfEntriesPerHorse <= 0
      ) {
        this.eventLimitForm.maxNumberOfEntriesPerHorse = null;
      }
      if (
        !this.eventLimitForm.maxNumberOfAthletes ||
        Number.isNaN(this.eventLimitForm.maxNumberOfAthletes) ||
        this.eventLimitForm.maxNumberOfAthletes <= 0
      ) {
        this.eventLimitForm.maxNumberOfAthletes = null;
      }

      if (
        !this.eventLimitForm.maxNumberOfHorsesPerAthlete ||
        Number.isNaN(this.eventLimitForm.maxNumberOfHorsesPerAthlete) ||
        this.eventLimitForm.maxNumberOfHorsesPerAthlete <= 0
      ) {
        this.eventLimitForm.maxNumberOfHorsesPerAthlete = null;
      }

      const isNotNewLimit = ![
        "new-competition-limit",
        "new-event-limit"
      ].includes(this.eventLimitForm.id);

      if (this.eventLimitForm.id && isNotNewLimit) {
        this.eventLimitUpdate();
      } else {
        this.eventLimitCreate();
      }
    },
    async eventLimitCreate() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation eventLimitCreate(
              $eventId: ID!
              $limitScope: String!
              $maxNumberOfEntries: Int
              $maxNumberOfAthletes: Int
              $maxNumberOfEntriesPerHorse: Int
              $competitionIds: [ID!]
              $maxNumberOfHorsesPerAthlete: Int
            ) {
              eventLimitCreate(
                input: {
                  eventId: $eventId
                  limitScope: $limitScope
                  maxNumberOfEntries: $maxNumberOfEntries
                  maxNumberOfAthletes: $maxNumberOfAthletes
                  maxNumberOfEntriesPerHorse: $maxNumberOfEntriesPerHorse
                  competitionIds: $competitionIds
                  maxNumberOfHorsesPerAthlete: $maxNumberOfHorsesPerAthlete
                }
              ) {
                eventLimit {
                  id
                  limitScope
                  maxNumberOfEntries
                  maxNumberOfAthletes
                  maxNumberOfHorsesPerAthlete
                  maxNumberOfEntriesPerHorse
                  allowedMutations
                  competitionIds
                  competitions {
                    id
                    status
                    limitEntriesForPublicRegistration
                    maxNumberOfHorsesPerAthlete
                    maxNumberOfEntriesPerHorse
                  }
                }
              }
            }
          `,
          refetchQueries: [
            {
              query: gql`
                query eventRefetchEventLimits($id: ID) {
                  event(id: $id) {
                    id
                    eventLimits {
                      ${this.$options.fragments.eventLimitFields}
                    }
                  }
                }
              `,
              variables: {
                id: this.event.id
              }
            }
          ],
          awaitRefetchQueries: true,
          variables: {
            eventId: this.event.id,
            limitScope: this.eventLimitForm.limitScope,
            maxNumberOfEntries: this.eventLimitForm.maxNumberOfEntries,
            maxNumberOfAthletes: this.eventLimitForm.maxNumberOfAthletes,
            competitionIds: this.eventLimitForm.competitionIds,
            maxNumberOfHorsesPerAthlete:
              this.eventLimitForm.maxNumberOfHorsesPerAthlete,
            maxNumberOfEntriesPerHorse:
              this.eventLimitForm.maxNumberOfEntriesPerHorse
          }
        });

        this.closeEventRuleLimitForm();
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.loading = false;
      }
    },
    async eventLimitUpdate() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation eventLimitUpdate(
              $id: ID!
              $maxNumberOfEntries: Int
              $maxNumberOfAthletes: Int
              $competitionIds: [ID!]
              $maxNumberOfHorsesPerAthlete: Int
              $maxNumberOfEntriesPerHorse: Int
            ) {
              eventLimitUpdate(
                input: {
                  id: $id
                  maxNumberOfEntries: $maxNumberOfEntries
                  maxNumberOfAthletes: $maxNumberOfAthletes
                  competitionIds: $competitionIds
                  maxNumberOfHorsesPerAthlete: $maxNumberOfHorsesPerAthlete
                  maxNumberOfEntriesPerHorse: $maxNumberOfEntriesPerHorse
                }
              ) {
                eventLimit {
                  id
                  maxNumberOfEntries
                  maxNumberOfAthletes
                  maxNumberOfHorsesPerAthlete
                  maxNumberOfEntriesPerHorse
                  allowedMutations
                  competitionIds
                  competitions {
                    id
                    status
                    limitEntriesForPublicRegistration
                    maxNumberOfHorsesPerAthlete
                    maxNumberOfEntriesPerHorse
                  }
                  event {
                    id
                    maxNumberOfEntries
                    maxNumberOfAthletes
                    maxNumberOfHorsesPerAthlete
                    maxNumberOfEntriesPerHorse
                  }
                }
              }
            }
          `,
          variables: {
            id: this.eventLimitForm.id,
            maxNumberOfEntries: this.eventLimitForm.maxNumberOfEntries,
            maxNumberOfAthletes: this.eventLimitForm.maxNumberOfAthletes,
            competitionIds: this.eventLimitForm.competitionIds,
            maxNumberOfHorsesPerAthlete:
              this.eventLimitForm.maxNumberOfHorsesPerAthlete,
            maxNumberOfEntriesPerHorse:
              this.eventLimitForm.maxNumberOfEntriesPerHorse
          }
        });
        this.closeEventRuleLimitForm();
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.loading = false;
      }
    },

    async deleteEventLimit(eventLimit) {
      const response = await this.$root.$confirm(
        this.$t(
          "events:common.registrationRulesAndLimits.discardLimit.heading",
          "Delete limit"
        ),
        this.$t(
          "events:common.registrationRulesAndLimits.discardLimit.question"
        )
      );
      if (!response) return;
      this.deleteLoading = eventLimit.id;

      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation eventLimitDelete($id: ID) {
              eventLimitDelete(input: { id: $id }) {
                eventLimit {
                  id
                  competitionIds
                  competitions {
                    id
                    status
                    limitEntriesForPublicRegistration
                    maxNumberOfHorsesPerAthlete
                    maxNumberOfEntriesPerHorse
                  }
                }
              }
            }
          `,
          refetchQueries: [
            {
              query: gql`
                query eventRefetchEventLimits($id: ID) {
                  event(id: $id) {
                    id
                    eventLimits {
                      id
                    }
                  }
                }
              `,
              variables: {
                id: this.event.id
              }
            }
          ],
          awaitRefetchQueries: true,
          variables: {
            id: eventLimit.id
          }
        });
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.deleteLoading = null;
      }
    }
  },
  fragments: {
    eventLimitEventFields: gql`
      fragment _ on Event {
        id
        competitions {
          id
          code

          difficultyLevelId
          difficultyLevel @client {
            id
            displayName
          }
          competitionRuleId
          competitionRule @client {
            id
            name
          }
          competitionLevelId
          competitionLevel @client {
            id
            name
          }
        }
        allowedMutations
      }
    `,
    eventLimitFields: gql`
      fragment _ on EventLimit {
        id
        eventId
        limitScope

        maxNumberOfEntries
        entriesRegistrationConfirmedCount

        maxNumberOfAthletes
        athletesRegistrationConfirmedCount

        maxNumberOfEntriesPerHorse
        maxNumberOfHorsesPerAthlete

        createdAt
        allowedMutations
        competitionIds
      }
    `
  }
};
</script>
<style lang="scss" scoped>
@media (max-width: 959px) {
  .mobile-smaller-heading {
    font-size: 16px;
  }
  .mobile-smaller-font {
    font-size: 12px;
  }
  .event-limits-mobile-table {
    tbody tr {
      td:first-child {
        font-size: 12px;
        padding-left: 10px !important;
      }
      td:last-child {
        padding-right: 10px !important;
      }
    }
  }
}
</style>
